import {
  addFilterToUrl,
  ProductCollectionFilter,
} from '@prisjakt/product-collection-urls';
import {
  UpdateProductCollectionFiltersAction,
  UPDATE_PRODUCT_COLLECTION_FILTERS,
} from 'src/areas/product-collection/actions';
import { TRACKING_TOGGLE_SAVE } from '../../areas/user/actions/tracking';
import {
  loadRecentlyVisited,
  LOAD_RECENTLY_VISITED,
  removeRecentlyVisitedItem,
  REMOVE_RECENTLY_VISITED_ITEM,
} from './actions';

import {
  readRecentlyVisited,
  writeRecentlyVisited,
  deleteRecentlyVisited,
  RecentlyVisitedItem,
  RecentlyVisitedProductCollection,
} from './storage';

const MAX_ITEMS = 100;

type InitialState = {
  trackingEnabled: boolean | undefined;
  items: RecentlyVisitedItem[];
  loaded: boolean;
};

type ProductData = {
  product: {
    id: number;
  };
};

type ProductCollectionData = {
  productCollection: {
    slug: string;
    id: string;
    url: string;
  };
};

type StateAction =
  | {
      type: '__DANGEROUS_CONFIG_RECEIVE';
      market: {
        trackingToggles: {
          name: string;
          default: boolean;
        }[];
      };
    }
  | {
      type: 'SESSION_RECEIVE';
      privacyCookie: {
        personalization: undefined | string;
      };
    }
  | {
      type: typeof TRACKING_TOGGLE_SAVE;
      settings: {
        personalization: unknown;
      };
    }
  | UpdateProductCollectionFiltersAction
  | {
      type: 'ROUTE_LOADED';
      route: {
        name: 'product' | 'productCollection';
      };
      data: ProductData | ProductCollectionData | undefined;
    }
  | ReturnType<typeof removeRecentlyVisitedItem>
  | ReturnType<typeof loadRecentlyVisited>;

export default (
  state: InitialState = {
    trackingEnabled: undefined,
    items: [],
    loaded: false,
  },
  action: StateAction
): InitialState => {
  if (!state) {
    return {
      trackingEnabled: undefined,
      loaded: false,
      items: [],
    };
  }

  switch (action.type) {
    case LOAD_RECENTLY_VISITED: {
      return {
        ...state,
        loaded: true,
        items: readRecentlyVisited(),
      };
    }
    case '__DANGEROUS_CONFIG_RECEIVE': {
      return {
        ...state,
        trackingEnabled: action.market.trackingToggles.find(
          t => t.name === 'personalization'
        )?.default,
      };
    }

    case 'SESSION_RECEIVE': {
      if (action.privacyCookie.personalization === undefined) {
        return state;
      }

      return {
        ...state,
        trackingEnabled: !!action.privacyCookie.personalization,
      };
    }

    case TRACKING_TOGGLE_SAVE: {
      if (!action.settings.personalization) {
        deleteRecentlyVisited();
      }
      break;
    }

    case UPDATE_PRODUCT_COLLECTION_FILTERS: {
      const newUrl = action.filters.reduce(
        (url, filter) =>
          addFilterToUrl(url, (filter as unknown) as ProductCollectionFilter),
        location.pathname
      );
      const readItems = readRecentlyVisited();

      const recentlyVisitedProductCollection = readItems.find(
        item => item.id === action.id
      ) as RecentlyVisitedProductCollection | undefined;

      if (!recentlyVisitedProductCollection) break;

      recentlyVisitedProductCollection.url = newUrl;

      const newItems = [
        recentlyVisitedProductCollection,
        ...readItems.filter(item => item.id !== action.id),
      ];

      writeRecentlyVisited(newItems);

      return {
        ...state,
        items: newItems,
      };
    }
    case 'ROUTE_LOADED': {
      if (
        !action.route ||
        !state.trackingEnabled ||
        !['product', 'productCollection'].includes(action.route.name) ||
        !action.data
      ) {
        break;
      }

      const readItems = readRecentlyVisited();

      const newItem = createRecentlyVisited(action.data);

      if (!newItem) break;

      const newItems = [
        newItem,
        ...readItems.filter(item => {
          if (newItem.type !== item.type) return item;

          if ('id' in item && 'id' in newItem) return item.id !== newItem.id;
          if ('slug' in item && 'slug' in newItem)
            return item.slug !== newItem.slug;

          return false;
        }),
      ].slice(0, MAX_ITEMS);

      writeRecentlyVisited(newItems);

      return {
        ...state,
        items: newItems,
      };
    }
    case REMOVE_RECENTLY_VISITED_ITEM: {
      const readItems = readRecentlyVisited();
      const newItems = readItems.filter(item => item.id !== action.id);

      writeRecentlyVisited(newItems);

      return {
        ...state,
        items: newItems,
      };
    }
    default:
      break;
  }

  return state;
};

function createRecentlyVisited(
  data: ProductData | ProductCollectionData
): RecentlyVisitedItem | null {
  const date = Date.now();

  if ('product' in data && data.product) {
    return { type: 'product', date, id: data.product.id };
  }
  if ('productCollection' in data && data.productCollection) {
    return {
      type: 'productCollection',
      date,
      id: data.productCollection.id,
      slug: data.productCollection.slug,
      url: data.productCollection.url,
    };
  }
  return null;
}
